import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import SEO from '../components/SEO';
import SchemaMarkup from '../components/SchemaMarkup';

const DARK = '#08111F';

/* Architecture visualization — the hero's "engineering visual".
   A correct, directed pipeline: the Hyvä/React storefront renders the Magento
   commerce core, which syncs to the ERP through an integration layer; Magento owns
   its datastore, and everything runs on shared infrastructure. */
function ArchitectureDiagram() {
  const { t } = useTranslation();
  const nodes = [
    { x: 40, y: 80, w: 210, h: 72, label: t('home_page.diagram.storefront'), sub: 'Hyvä · React' },
    { x: 290, y: 80, w: 210, h: 72, label: 'Magento 2', sub: t('home_page.diagram.commerce_core') },
    { x: 540, y: 80, w: 210, h: 72, label: t('home_page.diagram.integration'), sub: 'REST · SOAP', hot: true },
    { x: 790, y: 80, w: 210, h: 72, label: 'ERP', sub: 'Merit · Directo' },
    { x: 290, y: 244, w: 210, h: 72, label: t('home_page.diagram.data'), sub: 'SQL · Redis' }
  ];
  const links = [
    'M250,116 L290,116',   // storefront → magento
    'M500,116 L540,116',   // magento → integration
    'M750,116 L790,116',   // integration → ERP
    'M395,152 L395,244'    // magento → data store
  ];

  return (
    <>
    {/* Desktop / tablet: horizontal pipeline */}
    <svg viewBox="0 0 1040 420" role="img" aria-label={t('home_page.diagram.aria')} className="hidden h-auto w-full sm:block">
      <defs>
        <radialGradient id="mpxGlow" cx="52%" cy="30%" r="55%">
          <stop offset="0%" stopColor="#60A5FA" stopOpacity="0.16" />
          <stop offset="100%" stopColor="#60A5FA" stopOpacity="0" />
        </radialGradient>
        <marker id="mpxArrow" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6.5" markerHeight="6.5" orient="auto-start-reverse">
          <path d="M0,1 L9,5 L0,9 z" fill="#60A5FA" />
        </marker>
      </defs>
      <rect x="0" y="0" width="1040" height="420" fill="url(#mpxGlow)" aria-hidden="true" />

      <g aria-hidden="true" fill="none">
        {links.map((d, i) => (
          <g key={i}>
            <path d={d} stroke="rgba(148,163,184,0.22)" strokeWidth="1.5" markerEnd="url(#mpxArrow)" />
            <path d={d} className="mpx-flow" stroke="#60A5FA" strokeWidth="1.5" />
          </g>
        ))}
      </g>

      {/* Infrastructure foundation */}
      <g aria-hidden="true">
        <rect x="40" y="340" width="960" height="58" rx="14" fill="rgba(255,255,255,0.03)" stroke="rgba(255,255,255,0.08)" />
        <circle cx="70" cy="369" r="4" fill="#60A5FA" className="mpx-pulse" />
        <text x="92" y="374" fill="#94A3B8" fontSize="15" fontWeight="600" fontFamily="ui-monospace, monospace" letterSpacing="1">{t('home_page.diagram.infra')}</text>
      </g>

      {nodes.map((n, i) => (
        <g key={i} aria-hidden="true">
          <rect x={n.x} y={n.y} width={n.w} height={n.h} rx="16"
            fill={n.hot ? '#12213B' : '#0D1B30'}
            stroke={n.hot ? 'rgba(96,165,250,0.55)' : 'rgba(96,165,250,0.22)'} strokeWidth={n.hot ? 1.5 : 1} />
          <circle cx={n.x + 22} cy={n.y + 29} r="4" fill="#60A5FA" className={n.hot ? 'mpx-pulse' : ''} />
          <text x={n.x + 40} y={n.y + 34} fill="#E2E8F0" fontSize="19" fontWeight="700">{n.label}</text>
          <text x={n.x + 22} y={n.y + 55} fill="#64748B" fontSize="13" fontFamily="ui-monospace, monospace">{n.sub}</text>
        </g>
      ))}
    </svg>

    {/* Mobile: vertical stack, larger text so labels stay readable on phones */}
    <svg viewBox="0 0 360 550" role="img" aria-label={t('home_page.diagram.aria')} className="h-auto w-full sm:hidden">
      <defs>
        <marker id="mpxArrowM" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
          <path d="M0,1 L9,5 L0,9 z" fill="#60A5FA" />
        </marker>
      </defs>
      <g aria-hidden="true" fill="none">
        {['M180,92 L180,132', 'M180,210 L180,250', 'M180,328 L180,368', 'M180,446 L180,470'].map((d, i) => (
          <g key={i}>
            <path d={d} stroke="rgba(148,163,184,0.22)" strokeWidth="1.6" markerEnd="url(#mpxArrowM)" />
            <path d={d} className="mpx-flow" stroke="#60A5FA" strokeWidth="1.6" />
          </g>
        ))}
      </g>
      <g aria-hidden="true">
        <rect x="28" y="470" width="304" height="64" rx="16" fill="rgba(255,255,255,0.03)" stroke="rgba(255,255,255,0.08)" />
        <circle cx="52" cy="502" r="5" fill="#60A5FA" className="mpx-pulse" />
        <text x="74" y="508" fill="#94A3B8" fontSize="16" fontWeight="600" fontFamily="ui-monospace, monospace" letterSpacing="1">{t('home_page.diagram.infra')}</text>
      </g>
      {[
        { y: 14, label: t('home_page.diagram.storefront'), sub: 'Hyvä · React' },
        { y: 132, label: 'Magento 2', sub: t('home_page.diagram.commerce_core') },
        { y: 250, label: t('home_page.diagram.integration'), sub: 'REST · SOAP', hot: true },
        { y: 368, label: 'ERP', sub: 'Merit · Directo' }
      ].map((n, i) => (
        <g key={i} aria-hidden="true">
          <rect x="28" y={n.y} width="304" height="78" rx="18"
            fill={n.hot ? '#12213B' : '#0D1B30'}
            stroke={n.hot ? 'rgba(96,165,250,0.55)' : 'rgba(96,165,250,0.22)'} strokeWidth={n.hot ? 1.5 : 1} />
          <circle cx="54" cy={n.y + 34} r="5" fill="#60A5FA" className={n.hot ? 'mpx-pulse' : ''} />
          <text x="74" y={n.y + 41} fill="#E2E8F0" fontSize="23" fontWeight="700">{n.label}</text>
          <text x="54" y={n.y + 64} fill="#64748B" fontSize="15" fontFamily="ui-monospace, monospace">{n.sub}</text>
        </g>
      ))}
    </svg>
    </>
  );
}

export default function Home() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'et' ? '/et' : '/en';

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const expertise = t('home_page.expertise.items', { returnObjects: true });
  const expertiseList = Array.isArray(expertise) ? expertise : [];
  const expertiseLinks = [
    i18n.language === 'et' ? '/e-poe-tegemine' : '/ecommerce-development',
    '/services',
    '/services'
  ];

  const principles = t('home_page.principles.items', { returnObjects: true });
  const principleList = Array.isArray(principles) ? principles : [];

  const steps = t('home_page.process.steps', { returnObjects: true });
  const stepList = Array.isArray(steps) ? steps : [];

  const clients = t('home_page.clients.items', { returnObjects: true });
  const clientList = Array.isArray(clients) ? clients : [];

  const techStack = t('home_page.tech_stack', { returnObjects: true });
  const techList = Array.isArray(techStack) ? techStack : [];

  const csMetrics = t('case_studies.projects.eleberk-website.metrics', { returnObjects: true });
  const csMetricList = Array.isArray(csMetrics) ? csMetrics : [];

  return (
    <div className="bg-white text-slate-600">
      <SEO title={t('home_page.title')} description={t('home_page.meta_desc')} />
      <SchemaMarkup type="organization" />
      <SchemaMarkup type="local-business" />

      <style>{`
        @keyframes mpxDash { to { stroke-dashoffset: -24; } }
        .mpx-flow { stroke-dasharray: 3 9; stroke-linecap: round; animation: mpxDash 1.3s linear infinite; }
        @keyframes mpxPulse { 0%,100%{ opacity:.4 } 50%{ opacity:1 } }
        .mpx-pulse { animation: mpxPulse 3s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce){ .mpx-flow, .mpx-pulse { animation: none; } }
      `}</style>

      {/* 1 — HERO */}
      <section className="relative overflow-hidden px-6 pt-28 pb-12 md:pt-32 md:pb-16" style={{ backgroundColor: DARK }}>
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.4]" style={{ backgroundImage: 'linear-gradient(rgba(148,163,184,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.06) 1px, transparent 1px)', backgroundSize: '54px 54px' }} />
        <div aria-hidden="true" className="pointer-events-none absolute -top-48 left-1/2 h-[42rem] w-[42rem] -translate-x-1/2 rounded-full bg-blue-600/20 blur-[130px]" />

        <div className="relative mx-auto max-w-6xl text-center">
          <h1 className="mx-auto max-w-5xl text-4xl font-bold leading-[1.08] tracking-tight text-white sm:text-5xl md:text-6xl">
            {t('home_page.hero_title')}
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-slate-300 text-pretty md:text-xl motion-safe:animate-fade-in-up" style={{ animationDelay: '80ms', animationFillMode: 'both' }}>
            {t('home_page.hero_subtitle')}
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row motion-safe:animate-fade-in-up" style={{ animationDelay: '160ms', animationFillMode: 'both' }}>
            <Link to={`${lang}/contact`} className="flex w-full items-center justify-center gap-2 rounded-2xl bg-blue-600 px-8 py-4 font-semibold text-white shadow-lg shadow-blue-900/40 transition-all hover:-translate-y-0.5 hover:bg-blue-500 sm:w-auto">
              {t('home_page.hero_cta')} <ArrowRightIcon className="h-5 w-5" />
            </Link>
            <Link to={`${lang}/services`} className="flex w-full items-center justify-center rounded-2xl border border-white/15 px-8 py-4 font-semibold text-white transition-all hover:border-white/40 hover:bg-white/5 sm:w-auto">
              {t('home_page.hero_cta_secondary')}
            </Link>
          </div>
        </div>

        <div className="relative mx-auto mt-12 max-w-6xl motion-safe:animate-fade-in-up" style={{ animationDelay: '260ms', animationFillMode: 'both' }}>
          <div aria-hidden="true" className="pointer-events-none absolute -inset-6 rounded-[2rem] bg-blue-500/5 blur-2xl" />
          <div className="relative rounded-3xl border border-white/10 bg-white/[0.02] p-5 md:p-8">
            <ArchitectureDiagram />
          </div>
        </div>

        {/* 2 — TRUSTED BY (minimal, on the dark surface) */}
        <div className="relative mx-auto mt-12 max-w-6xl border-t border-white/10 pt-8">
          <div className="flex flex-col items-center gap-6 text-center md:flex-row md:justify-between md:text-left">
            <div>
              <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{t('home_page.trusted_by')}</p>
              <div className="mt-3 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 md:justify-start">
                {clientList.map((c) => (
                  <a key={c.url} href={c.url} target="_blank" rel="noopener noreferrer" className="text-base font-semibold text-slate-300 transition-colors hover:text-white">{c.name}</a>
                ))}
              </div>
            </div>
            <div className="md:text-right">
              <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{t('home_page.built_on')}</p>
              <p className="mt-3 max-w-sm text-sm text-slate-400">{techList.slice(0, 6).join(' · ')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* 3 — CORE EXPERTISE (three large cards) */}
      <section className="px-6 py-16 md:py-20">
        <div className="mx-auto max-w-6xl">
          <div className="max-w-2xl">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">{t('home_page.expertise.label')}</p>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 text-balance md:text-5xl">{t('home_page.expertise.title')}</h2>
          </div>

          <div className="mt-10 grid gap-5 lg:grid-cols-3">
            {expertiseList.map((item, i) => (
              <Link key={i} to={`${lang}${expertiseLinks[i]}`} className="group flex flex-col rounded-3xl bg-slate-50 p-7 shadow-[0_1px_3px_rgba(15,23,42,0.06)] ring-1 ring-slate-200/60 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-slate-200/70 md:p-8">
                <span aria-hidden="true" className="text-sm font-semibold text-blue-600">{String(i + 1).padStart(2, '0')}</span>
                <h3 className="mt-4 text-2xl font-bold text-slate-900">{item.title}</h3>
                <p className="mt-3 flex-grow text-[1.05rem] leading-relaxed text-slate-600">{item.desc}</p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {(item.tags || []).map((tag) => (
                    <span key={tag} className="rounded-lg bg-white px-2.5 py-1 font-mono text-xs font-medium text-slate-500 ring-1 ring-slate-200">{tag}</span>
                  ))}
                </div>
                <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600">
                  {t('home_page.read_more')} <ArrowRightIcon className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 4 — ENGINEERING PRINCIPLES (editorial blocks) */}
      <section className="bg-slate-50 px-6 py-16 md:py-20">
        <div className="mx-auto max-w-5xl">
          <div className="max-w-2xl">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">{t('home_page.principles.label')}</p>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 text-balance md:text-5xl">{t('home_page.principles.title')}</h2>
          </div>

          <div className="mt-10 space-y-px">
            {principleList.map((p, i) => (
              <div key={i} className="grid gap-3 border-t border-slate-200 py-7 md:grid-cols-12 md:gap-10 md:py-8">
                <h3 className="text-2xl font-bold tracking-tight text-slate-900 md:col-span-5 md:text-3xl">{p.title}</h3>
                <p className="text-lg leading-relaxed text-slate-600 md:col-span-7">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5 — FEATURED CASE STUDY */}
      <section className="px-6 py-16 md:py-20">
        <div className="mx-auto grid max-w-6xl items-center gap-8 lg:grid-cols-2 lg:gap-12">
          <div className="relative order-2 lg:order-1">
            <div aria-hidden="true" className="pointer-events-none absolute -inset-4 rounded-[2rem] bg-blue-500/10 blur-2xl" />
            <div className="relative overflow-hidden rounded-2xl shadow-2xl shadow-slate-300/60 ring-1 ring-slate-200">
              <div className="flex items-center gap-1.5 border-b border-slate-200 bg-slate-100 px-4 py-3">
                <span className="h-3 w-3 rounded-full bg-slate-300" />
                <span className="h-3 w-3 rounded-full bg-slate-300" />
                <span className="h-3 w-3 rounded-full bg-slate-300" />
              </div>
              <img src="/images/blog/eleberk-screenshot.webp" alt={t('case_studies.projects.eleberk-website.title')} width="1200" height="750" loading="lazy" className="aspect-[16/10] w-full object-cover object-top" />
            </div>
          </div>

          <div className="order-1 lg:order-2">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">{t('home_page.case_study.label')}</p>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 text-balance md:text-4xl">{t('case_studies.projects.eleberk-website.title')}</h2>
            <p className="mt-4 text-lg leading-relaxed text-slate-600">{t('case_studies.projects.eleberk-website.summary')}</p>

            <div className="mt-6 flex flex-wrap gap-x-10 gap-y-5">
              {csMetricList.map((m) => (
                <div key={m.label}>
                  <div className="text-3xl font-bold tracking-tight text-slate-900 [font-variant-numeric:tabular-nums]">{m.value}</div>
                  <div className="mt-1 text-sm text-slate-500">{m.label}</div>
                </div>
              ))}
            </div>

            <Link to={`${lang}/case-studies/eleberk-website`} className="mt-7 inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-7 py-3.5 font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-slate-800">
              {t('home_page.case_study.cta')} <ArrowRightIcon className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* 7 — PROCESS (timeline) */}
      <section className="bg-slate-50 px-6 py-16 md:py-20">
        <div className="mx-auto max-w-6xl">
          <div className="max-w-2xl">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">{t('home_page.process.label')}</p>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 text-balance md:text-5xl">{t('home_page.process.title')}</h2>
          </div>

          <ol className="relative mt-10 grid gap-8 md:grid-cols-5 md:gap-6">
            <div aria-hidden="true" className="absolute left-0 right-0 top-3 hidden h-px bg-slate-200 md:block" />
            {stepList.map((s, i) => (
              <li key={i} className="relative">
                <span aria-hidden="true" className="relative z-10 flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 text-[0.7rem] font-bold text-white ring-4 ring-slate-50">{i + 1}</span>
                <h3 className="mt-5 text-lg font-bold text-slate-900">{s.title}</h3>
                <p className="mt-2 text-[0.95rem] leading-relaxed text-slate-600">{s.desc}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* 8 — FINAL CTA */}
      <section className="relative overflow-hidden px-6 py-20 md:py-24 text-center" style={{ backgroundColor: DARK }}>
        <div aria-hidden="true" className="pointer-events-none absolute -top-32 left-1/2 h-80 w-[80%] max-w-3xl -translate-x-1/2 rounded-full bg-blue-600/25 blur-[120px]" />
        <div className="relative mx-auto max-w-3xl">
          <h2 className="text-3xl font-bold tracking-tight text-white text-balance md:text-5xl">{t('home_page.cta_banner.title')}</h2>
          <p className="mx-auto mt-5 max-w-xl text-lg leading-relaxed text-slate-300 text-pretty">{t('home_page.cta_banner.subtitle')}</p>
          <Link to={`${lang}/contact`} className="mt-8 inline-flex items-center gap-2 rounded-2xl bg-blue-600 px-9 py-4 text-lg font-semibold text-white shadow-lg shadow-blue-900/40 transition-all hover:-translate-y-0.5 hover:bg-blue-500">
            {t('home_page.cta_banner.btn')} <ArrowRightIcon className="h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
